
import { Link } from "react-router-dom";
import { UserCircle, Target, Video, Flag, Home, Menu, ChevronDown, HelpCircle, MapPin, Users, Shield, Presentation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { LanguageSelector } from "@/components/ui/language-selector";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NavigationMenu = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { t } = useTranslation(['navigation', 'dashboard']);
  const { isAdmin } = useIsAdmin();

  // Log to check if isMobile is working
  console.log("Is mobile:", isMobile);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate("/auth");
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('dashboard:errors.loadingError'),
        description: error.message,
      });
    }
  };

  const navigationItems = [
    { to: "/", icon: <Home className="h-4 w-4 mr-2" />, label: t('navigation:menu.home') },
    {
      isSubmenu: true,
      icon: <Flag className="h-4 w-4 mr-2" />,
      label: t('navigation:menu.training'),
      submenu: [
        { to: "/training", label: t('navigation:levels.general') },
        { to: "/training/level3", label: t('navigation:levels.level3') },
        { to: "/training/level2", label: t('navigation:levels.level2') },
        { to: "/training/level1", label: t('navigation:levels.level1') },
        { href: "https://parcours.golf-progress-calculator.com/", label: t('navigation:menu.courseStats'), icon: <Flag className="h-4 w-4 mr-2 text-green-600" />, external: true },
      ]
    },
    {
      isSubmenu: true,
      icon: <Target className="h-4 w-4 mr-2" />,
      label: t('navigation:menu.testPelz'),
      submenu: [
        { to: "/putting", label: t('navigation:menu.putting') },
        { to: "/approaches", label: t('navigation:menu.approaches') },
        { to: "/results", label: t('navigation:menu.results') },
      ]
    },
    {
      isSubmenu: true,
      icon: <Video className="h-4 w-4 mr-2" />,
      label: t('navigation:menu.videos'),
      submenu: [
        { to: "/videos", label: t('navigation:menu.myVideos') },
        ...(isAdmin ? [{ to: "/presentation", label: t('navigation:menu.presentationVideos'), icon: <Presentation className="h-4 w-4 mr-2" /> }] : []),
      ]
    },
    { to: "/ball-distance", icon: <MapPin className="h-4 w-4 mr-2" />, label: t('navigation:menu.distance') },
    { to: "/subscription", icon: <Shield className="h-4 w-4 mr-2" />, label: t('navigation:menu.subscription') },
    {
      isSubmenu: true,
      icon: <Users className="h-4 w-4 mr-2" />,
      label: t('navigation:menu.supervision'),
      submenu: [
        { to: "/coach-dashboard", label: t('navigation:menu.coachDashboard') },
        { to: "/supervision-management", label: t('navigation:menu.supervisionManagement') },
        { to: "/training-results", label: t('navigation:menu.trainingResults') },
      ]
    },
    { to: "/help", icon: <HelpCircle className="h-4 w-4 mr-2" />, label: t('navigation:menu.help') },
  ];

  const DesktopMenu = () => (
    <div className="flex gap-2">
      {navigationItems.map((item, index) => 
        item.isSubmenu ? (
          <DropdownMenu key={index}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center">
                {item.icon}
                {item.label}
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white z-50">
              {item.submenu?.map((subItem: any, subIndex) => (
                <DropdownMenuItem key={subIndex} asChild>
                  {subItem.external ? (
                    <a href={subItem.href} target="_blank" rel="noopener noreferrer" className="flex items-center w-full">
                      {subItem.icon}
                      {subItem.label}
                    </a>
                  ) : (
                    <Link to={subItem.to} className="flex items-center w-full">
                      {subItem.label}
                    </Link>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button key={index} asChild variant="ghost" size="sm">
            <Link to={item.to}>
              {item.icon}
              {item.label}
            </Link>
          </Button>
        )
      )}
      <Button asChild variant="outline" size="sm">
        <Link to="/profile">
          <UserCircle className="mr-2 h-4 w-4" />
          {t('navigation:menu.profile')}
        </Link>
      </Button>
      <Button variant="outline" size="sm" onClick={handleSignOut}>
        {t('navigation:menu.signOut')}
      </Button>
    </div>
  );

  const MobileMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon">
          <Menu className="h-5 w-5" />
          <span className="sr-only">{t('navigation:menu.menu')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-white border border-gray-200 shadow-lg z-[200]">
        {navigationItems.map((item, index) => 
          item.isSubmenu ? (
            <DropdownMenuSub key={index}>
              <DropdownMenuSubTrigger className="flex items-center font-medium text-gray-800">
                {item.icon}
                {item.label}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent 
                className="bg-white border border-gray-200 shadow-lg z-[300]"
                sideOffset={-150}
                alignOffset={0}
              >
                {item.submenu?.map((subItem: any, subIndex) => (
                  <DropdownMenuItem 
                    key={subIndex} 
                    asChild 
                    className="text-gray-800 font-medium hover:bg-golf-100 hover:text-golf-700"
                  >
                    {subItem.external ? (
                      <a href={subItem.href} target="_blank" rel="noopener noreferrer" className="flex items-center w-full py-2 px-2">
                        {subItem.icon}
                        {subItem.label}
                      </a>
                    ) : (
                      <Link to={subItem.to} className="flex items-center w-full py-2 px-2">
                        {subItem.label}
                      </Link>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ) : (
            <DropdownMenuItem key={index} asChild className="font-medium text-gray-800 hover:bg-golf-100">
              <Link to={item.to} className="flex items-center w-full">
                {item.icon}
                {item.label}
              </Link>
            </DropdownMenuItem>
          )
        )}
        <DropdownMenuSeparator className="bg-gray-200" />
        <DropdownMenuItem asChild className="mt-2 font-medium text-gray-800 hover:bg-golf-100">
          <Link to="/profile" className="flex items-center w-full">
            <UserCircle className="mr-2 h-4 w-4" />
            {t('navigation:menu.profile')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem className="mt-2 text-red-500 font-medium hover:bg-red-50" onClick={handleSignOut}>
          {t('navigation:menu.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="bg-white shadow-md py-3 px-6 sticky top-0 z-10">
      <div className="container mx-auto">
        <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <Flag className="h-6 w-6 text-golf-600" />
            <span className="text-sm font-medium text-golf-800">{t('navigation:appName')}</span>
          </div>
          <LanguageSelector />
        </div>
          
          {isMobile ? <MobileMenu /> : <DesktopMenu />}
        </div>
      </div>
    </div>
  );
};

export default NavigationMenu;
